import { RotateCcw } from 'lucide-react';
import type { FeedbackDimensions } from '@ederest/contracts';
import { Button } from '../../shared/ui/Button.js';
import { PERIOD_PRESETS, type useAnalyticsFilters } from '../../shared/api/useAnalyticsFilters.js';
import '../../shared/ui/filters.css';

type Filters = ReturnType<typeof useAnalyticsFilters>;

interface FeedbackFilterBarProps {
  filters: Filters;
  dimensions: FeedbackDimensions | undefined;
}

const VIEWPORT_LABELS: Record<string, string> = {
  mobile: 'Téléphone',
  tablet: 'Tablette',
  desktop: 'Ordinateur',
};
const LANGUAGE_LABELS: Record<string, string> = { fr: 'Français', ar: 'Arabe', zgh: 'Amazighe' };

/**
 * Segmentation of the collected feedback (functional §5.8): issuing entity and
 * announcement type, plus the shared criteria. Options come from the feedback
 * actually collected; the filters live in the URL like the dashboard's.
 */
export function FeedbackFilterBar({ filters, dimensions }: FeedbackFilterBarProps) {
  const { filters: value, set, setPeriod, reset, activeCount, activePreset } = filters;

  return (
    <div className="filters" role="group" aria-label="Filtres des avis recueillis">
      <div className="filters__presets" role="group" aria-label="Période">
        {PERIOD_PRESETS.map((days) => (
          <button
            key={days}
            type="button"
            className={`filters__preset ${activePreset === days ? 'filters__preset--active' : ''}`}
            aria-pressed={activePreset === days}
            onClick={() => setPeriod(days)}
          >
            {days} jours
          </button>
        ))}
      </div>

      <label className="filters__field">
        <span>Administration émettrice</span>
        <select
          aria-label="Filtrer par entité émettrice"
          value={value.entityKey ?? ''}
          onChange={(e) => set({ entityKey: e.target.value })}
        >
          <option value="">Toutes les administrations</option>
          {(dimensions?.entityKeys ?? []).map((entity) => (
            <option key={entity} value={entity}>
              {entity}
            </option>
          ))}
        </select>
      </label>

      <label className="filters__field">
        <span>Type d'annonce</span>
        <select
          aria-label="Filtrer par type d'annonce"
          value={value.announcementType ?? ''}
          onChange={(e) => set({ announcementType: e.target.value })}
        >
          <option value="">Tous les types</option>
          {(dimensions?.announcementTypes ?? []).map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </label>

      <label className="filters__field">
        <span>Langue</span>
        <select
          aria-label="Filtrer par langue"
          value={value.language ?? ''}
          onChange={(e) => set({ language: e.target.value })}
        >
          <option value="">Toutes les langues</option>
          {(dimensions?.languages ?? []).map((language) => (
            <option key={language} value={language}>
              {LANGUAGE_LABELS[language] ?? language}
            </option>
          ))}
        </select>
      </label>

      <label className="filters__field">
        <span>Appareil</span>
        <select
          aria-label="Filtrer par appareil"
          value={value.viewport ?? ''}
          onChange={(e) => set({ viewport: e.target.value })}
        >
          <option value="">Tous les appareils</option>
          {(dimensions?.viewports ?? []).map((viewport) => (
            <option key={viewport} value={viewport}>
              {VIEWPORT_LABELS[viewport] ?? viewport}
            </option>
          ))}
        </select>
      </label>

      <label className="filters__field">
        <span>Version de guidance</span>
        <select
          aria-label="Filtrer par version de guidance"
          value={value.configurationVersion !== undefined ? String(value.configurationVersion) : ''}
          onChange={(e) => set({ configurationVersion: e.target.value })}
        >
          <option value="">Toutes les versions</option>
          {(dimensions?.configurationVersions ?? []).map((version) => (
            <option key={version} value={version}>
              Version {version}
            </option>
          ))}
        </select>
      </label>

      {activeCount > 0 && (
        <Button variant="ghost" icon={<RotateCcw />} onClick={reset} aria-label="Réinitialiser les filtres">
          Réinitialiser
        </Button>
      )}
    </div>
  );
}
